import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { getStudioNameAvailability } from "../../api/signupApi";
import { i18_SIGNUP } from "../../i18nReferences";

/**
 * Tracks whether the studio name entered in the signup form is already taken.
 * Every name the server reported as taken is kept; the error shows while the field holds one of them.
 * Answers may arrive out of order, and each only adds or removes its own name, so a late answer for
 * an old name never changes the error for the current one.
 */
export const useStudioNameAvailability = (form) => {
    const { t } = useTranslation([i18_SIGNUP]);
    const [takenNames, setTakenNames] = useState([]);
    const currentName = useWatch({ control: form.control, name: "studioName" });
    const { mutateAsync: checkAvailability } = useMutation({ mutationFn: getStudioNameAvailability });

    const markStudioNameTaken = useCallback(
        (name) => setTakenNames((previous) => (previous.includes(name) ? previous : [...previous, name])),
        []
    );

    // Ask the server on blur, but only for a name that passes the form validation.
    const handleStudioNameBlur = useCallback(async () => {
        const name = form.getValues("studioName").trim();
        if (!(await form.trigger("studioName")) || !name) {
            return;
        }
        try {
            const available = await checkAvailability(name);
            if (available) {
                setTakenNames((previous) => previous.filter((takenName) => takenName !== name));
            } else {
                markStudioNameTaken(name);
            }
        } catch {
            // The server checks again on submit, so a failed lookup must not block the form.
        }
    }, [form, checkAvailability, markStudioNameTaken]);

    const studioNameInUseError =
        takenNames.includes(currentName?.trim()) ? t("YUP.ERROR.STUDIO_NAME_IN_USE") : null;

    return { studioNameInUseError, handleStudioNameBlur, markStudioNameTaken };
};
